import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useVisibility } from '@/hooks/useVisibility';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Switch } from '@/components/ui/switch';
import { getDateRangeFromPreset, type DatePreset } from '@/utils/datePresets';
import { cn } from '@/lib/utils';
import { ArrowRight, Briefcase, FileText, Users } from 'lucide-react';

// Executive Components
import { DailyHuddleRow } from '@/components/dashboard/DailyHuddleRow';
import { CompanyOverviewTab } from '@/components/dashboard/CompanyOverviewTab';
import { TechniciansTab } from '@/components/dashboard/TechniciansTab';
import { OnTheWayCard } from '@/components/dashboard/OnTheWayCard';
import { LeadsDashboard } from '@/components/leads/LeadsDashboard';

type DashboardPreset = 'today' | 'this_week' | 'this_month' | 'quarter' | 'custom';

const QUICK_LINKS = [
  { to: '/leads', label: 'Leads', description: 'Work your pipeline and follow-ups', icon: Users },
  { to: '/jobs', label: 'Jobs', description: "Today's bookings and open jobs", icon: Briefcase },
  { to: '/estimates', label: 'Estimates', description: 'Quotes waiting on a customer', icon: FileText },
];

const PRESET_SEGMENTS: { key: Exclude<DashboardPreset, 'custom'>; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This week' },
  { key: 'this_month', label: 'This month' },
  { key: 'quarter', label: 'Quarter' },
];

export default function Dashboard() {
  const { timezone, startOfDay, endOfDay, formatDate, parseDate } = useTimezoneUtils();
  const { locale } = useCompanyLocale();
  const { canSeeCompanyFinancials } = useVisibility();
  const navigate = useNavigate();

  // Initialize with "This Month" range
  const initialRange = getDateRangeFromPreset('this_month', timezone, locale.weekStartsOn);
  const [dateFrom, setDateFrom] = useState<Date>(initialRange.from);
  const [dateTo, setDateTo] = useState<Date>(initialRange.to);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [activePreset, setActivePreset] = useState<DashboardPreset>('this_month');

  // Quarter-to-date in the company timezone (no 'quarter' preset exists in datePresets)
  const getQuarterRange = () => {
    const now = new Date();
    const monthInTz = parseInt(formatDate(now, 'M'), 10);
    const yearInTz = formatDate(now, 'yyyy');
    const quarterStartMonth = monthInTz - ((monthInTz - 1) % 3);
    const quarterStart = parseDate(`${yearInTz}-${String(quarterStartMonth).padStart(2, '0')}-01`);
    return { from: startOfDay(quarterStart), to: endOfDay(now) };
  };

  const handlePresetSelect = (preset: Exclude<DashboardPreset, 'custom'>) => {
    setActivePreset(preset);
    const range =
      preset === 'quarter'
        ? getQuarterRange()
        : getDateRangeFromPreset(preset as DatePreset, timezone, locale.weekStartsOn);
    if (range.from) setDateFrom(range.from);
    if (range.to) setDateTo(range.to);
  };

  const handleDateChange = (from: Date | undefined, to: Date | undefined) => {
    setActivePreset('custom');
    // "All Time" returns undefined on both ends: widen the range instead of ignoring it.
    setDateFrom(from ?? new Date(2000, 0, 1));
    setDateTo(to ?? endOfDay(new Date()));
  };

  const safeDateFrom = dateFrom || startOfDay(new Date());
  const safeDateTo = dateTo || endOfDay(new Date());

  const pillSegmentClass = (active: boolean) =>
    cn(
      'h-8 rounded-full px-3.5 text-[13px] font-semibold transition-colors',
      active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
    );

  return (
    <Layout>
      <div className="space-y-[22px]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {canSeeCompanyFinancials ? 'Executive dashboard' : 'Your day'}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {canSeeCompanyFinancials
                ? "Business performance and today's pulse"
                : 'Your leads, follow-ups and next steps'}
            </p>
          </div>

          {/* Segmented date control — presets drive the same dateFrom/dateTo state;
              the existing DateRangePicker stays as the Custom segment (full picker + compare) */}
          <div className="flex flex-wrap items-center gap-0.5 self-start rounded-full border bg-card p-1 shadow-sm xl:self-auto">
            {PRESET_SEGMENTS.map((segment) => (
              <button
                key={segment.key}
                type="button"
                onClick={() => handlePresetSelect(segment.key)}
                className={pillSegmentClass(activePreset === segment.key)}
              >
                {segment.label}
              </button>
            ))}
            <div className="mx-1 h-4 w-px bg-border" />
            <DateRangePicker
              dateFrom={safeDateFrom}
              dateTo={safeDateTo}
              onDateChange={handleDateChange}
              compareEnabled={compareEnabled}
              onCompareChange={setCompareEnabled}
              align="end"
              className={cn(
                'h-8 min-w-0 rounded-full border-0 bg-transparent px-3.5 text-[13px] font-medium shadow-none',
                activePreset === 'custom'
                  ? 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
                  : 'text-muted-foreground hover:bg-transparent hover:text-foreground'
              )}
            />
          </div>
        </div>

        {!canSeeCompanyFinancials ? (
          <>
            <OnTheWayCard />
            <LeadsDashboard
              dateFrom={safeDateFrom}
              dateTo={safeDateTo}
              compareEnabled={compareEnabled}
              onFollowUpFilterClick={() => navigate('/leads')}
            />
            <div className="grid gap-4 sm:grid-cols-3">
              {QUICK_LINKS.map(({ to, label, description, icon: Icon }) => (
                <Card
                  key={to}
                  className="cursor-pointer transition-colors hover:bg-muted/50"
                  onClick={() => navigate(to)}
                >
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold">{label}</p>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
        <>
        {/* 1. Daily Huddle - Always visible, Real-time Today */}
        <DailyHuddleRow />

        {/* Technicians on the road, with the ETA from the app (hidden when nobody is en route) */}
        <OnTheWayCard />

        {/* 2. Company Overview & Technicians */}
        <Tabs defaultValue="overview" className="w-full">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <TabsList className="h-auto rounded-full border bg-card p-1 text-muted-foreground shadow-sm">
              <TabsTrigger
                value="overview"
                className="rounded-full px-4 py-1.5 text-[13px] font-semibold data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-none"
              >
                Company overview
              </TabsTrigger>
              <TabsTrigger
                value="technicians"
                className="rounded-full px-4 py-1.5 text-[13px] font-semibold data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-none"
              >
                Technicians
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <label
                htmlFor="dashboard-compare"
                className="cursor-pointer select-none text-[13px] font-medium text-muted-foreground"
              >
                Compare with previous period
              </label>
              <Switch
                id="dashboard-compare"
                checked={compareEnabled}
                onCheckedChange={setCompareEnabled}
              />
            </div>
          </div>

          <TabsContent value="overview" className="mt-[22px]">
            <CompanyOverviewTab dateFrom={safeDateFrom} dateTo={safeDateTo} compareEnabled={compareEnabled} />
          </TabsContent>

          <TabsContent value="technicians" className="mt-[22px]">
            <TechniciansTab dateFrom={safeDateFrom} dateTo={safeDateTo} compareEnabled={compareEnabled} />
          </TabsContent>
        </Tabs>
        </>
        )}
      </div>
    </Layout>
  );
}
